import { useEffect, useState } from 'react';
import type { Content } from '@neopolis/engine';

import { T } from '../textes-interface.js';
import type { ClientProf, ProgressionIlot } from './api-prof.js';
import { INTERVALLE_RAFRAICHISSEMENT_MS } from './SuiviDeClasse.js';

/**
 * Mode projection.
 *
 * Affiché au vidéoprojecteur en fin de séance, pour lancer le débat : chaque
 * îlot, la solution qu'il a installée, et ce qu'elle a coûté à la ville.
 *
 * Tout est dimensionné pour être lu depuis le fond de la salle — le plan
 * demande au moins 28 px, les tailles utilisées ici commencent à 30.
 */
export function ModeProjection({
  content,
  client,
  classCode,
  pin,
  onQuitter,
}: {
  content: Content;
  client: ClientProf;
  classCode: string;
  pin: string;
  onQuitter: () => void;
}) {
  const [ilots, setIlots] = useState<readonly ProgressionIlot[]>([]);

  useEffect(() => {
    const rafraichir = async (): Promise<void> => {
      const suivi = await client.suivre(classCode, pin);
      if (suivi) {
        setIlots(suivi.ilots);
      }
    };
    void rafraichir();
    const minuteur = setInterval(() => void rafraichir(), INTERVALLE_RAFRAICHISSEMENT_MS);
    return () => clearInterval(minuteur);
  }, [client, classCode, pin]);

  const avecSolution = ilots.filter((ilot) => ilot.solutionDeployee !== null);

  return (
    <div className="min-h-screen bg-slate-900 p-8 text-white">
      <header className="flex items-start justify-between gap-6">
        <h1 className="text-5xl font-bold">{T.prof.solutionsChoisies}</h1>
        <button
          type="button"
          onClick={onQuitter}
          className="min-h-touch rounded-xl bg-white/10 px-5 text-3xl font-semibold text-white hover:bg-white/20"
        >
          {T.prof.quitterProjection}
        </button>
      </header>

      {avecSolution.length === 0 ? (
        <p className="mt-16 text-center text-4xl text-slate-300">{T.prof.aucuneSolutionEncore}</p>
      ) : (
        <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {avecSolution.map((ilot) => {
            const solution = content.solutions.find((s) => s.id === ilot.solutionDeployee);

            return (
              <li key={ilot.islandCode} className="rounded-3xl bg-white/10 p-6">
                <p className="font-mono text-4xl font-bold tracking-widest">{ilot.islandCode}</p>

                <p className="mt-3 text-5xl" aria-label={`${solution?.etoiles ?? 0} / 3`}>
                  <span aria-hidden="true">
                    {'★'.repeat(solution?.etoiles ?? 0)}
                    {'☆'.repeat(3 - (solution?.etoiles ?? 0))}
                  </span>
                </p>

                <ul className="mt-4 space-y-1 text-3xl">
                  {content.jauges.map((jauge) => (
                    <li key={jauge.id} className="flex items-center gap-3">
                      <span aria-hidden="true">{jauge.icone}</span>
                      <span className="tabular-nums">{ilot.gauges?.[jauge.id] ?? '—'}</span>
                      <span className="sr-only">{jauge.nom}</span>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
